import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, toastStore } from 'shared/ui'
import { type Integration, IntegrationsApi, type ISlackChannel } from 'entities/Integrations'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SendFromMultiStore } from 'widgets/SendFromMulti'

export interface IInboxSlackMapping {
  id: string
  inboxes: number[]
  slackChannelId: string
  isSaved: boolean
  error?: string
  sendFromMultiStore: SendFromMultiStore
  originalInboxes?: number[]
}

export class IntegrationSlackSettingsStore {
  private _allTeamInboxesMap: Map<number, IResponseInbox> = new Map()
  private _slackChannelsMap: Map<string, ISlackChannel> = new Map()
  private _mappings: IInboxSlackMapping[] = []
  private _loading = false

  constructor(private _integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await this.fetchAllInboxes()
    this.getChannels()
    await this.getMappings()

    runInAction(() => {
      this._loading = false
    })
  }

  get allTeamInboxes() {
    return Array.from(this._allTeamInboxesMap.values()).filter((item) => !item.is_rcs)
  }

  get slackChannels() {
    return Array.from(this._slackChannelsMap.values())
  }

  get mappings() {
    return this._mappings
  }

  get loading() {
    return this._loading
  }

  get availableInboxes() {
    const usedInboxIds = this.getTotalMappedInboxes()
    let actuallyAvailableInboxes: IResponseInbox[] = []

    if (this._mappings.length > 0) {
      const dropdownItems = this._mappings[0].sendFromMultiStore.dropdownItems
      const availableInboxIds = new Set<number>()

      dropdownItems.forEach((item) => {
        if (item.data?.inbox?.id) {
          availableInboxIds.add(item.data.inbox.id)
        }
      })

      actuallyAvailableInboxes = this.allTeamInboxes.filter((inbox) =>
        availableInboxIds.has(inbox.id)
      )
    } else {
      actuallyAvailableInboxes = this.allTeamInboxes
    }

    return actuallyAvailableInboxes.filter((inbox) => !usedInboxIds.has(inbox.id))
  }

  get canAddMapping() {
    if (this.isAllInboxesSelected) {
      return false
    }
    return this.slackChannels.length > this.getTotalMappedChannels()
  }

  get isAllInboxesSelected() {
    return this.availableInboxes.length === 0
  }

  get isEmptyMappings() {
    return this._mappings.some(
      (mapping) => this.getCurrentInboxes(mapping).length === 0 || mapping.slackChannelId === ''
    )
  }

  getTotalMappedInboxes() {
    const allMappedInboxIds = new Set<number>()
    this.mappings.forEach((mapping) => {
      const selectedInboxIds = this.getCurrentInboxes(mapping)
      selectedInboxIds.forEach((inboxId) => allMappedInboxIds.add(inboxId))
    })
    return allMappedInboxIds
  }

  getTotalMappedChannels() {
    const usedChannelIds = new Set()
    this.mappings.forEach((mapping) => {
      if (mapping.slackChannelId) {
        usedChannelIds.add(mapping.slackChannelId)
      }
    })
    return usedChannelIds.size
  }

  get tooltipMessage() {
    if (this.isAllInboxesSelected) {
      return 'All of your inboxes are already mapped'
    }
    if (this.slackChannels.length <= this.getTotalMappedChannels()) {
      return 'All of your Slack channels are already mapped'
    }
    return ''
  }

  getCurrentInboxes = (mapping: IInboxSlackMapping) => {
    return mapping.sendFromMultiStore.selectedInboxIds
  }

  isMappingFullfilled = (mapping: IInboxSlackMapping) => {
    return Boolean(this.getCurrentInboxes(mapping).length) && Boolean(mapping.slackChannelId)
  }

  shouldShowDeleteButton = (mapping: IInboxSlackMapping) => {
    return (
      this.mappings.length > 1 || (this.mappings.length === 1 && this.isMappingFullfilled(mapping))
    )
  }

  fetchAllInboxes = async () => {
    try {
      const { data } = await this._entities.inboxesStore.getTeamsAll()

      runInAction(() => {
        data.forEach((item) => {
          this._allTeamInboxesMap.set(item.id, item)
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  getChannels = async () => {
    try {
      const { data } = await IntegrationsApi.getSlackIntegrationChannels(this._integration.key)

      this._slackChannelsMap.clear()

      data.channels.forEach((item) => {
        this._slackChannelsMap.set(item.id, item)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  createSendFromMultiStore = (inboxes?: IResponseInbox[]) => {
    return new SendFromMultiStore(this._entities, {
      getDropdownItemsOptions: {
        excludeShortCode: true,
        disabledClosedTooltip: true,
        hideChildren: true,
      },
      preloadedInboxes: inboxes,
      isInboxMode: true,
    })
  }

  updateExcludeInboxIds = () => {
    const allUsedInboxIds = this.getTotalMappedInboxes()

    this._mappings.forEach((mapping) => {
      const currentSelectedIds = this.getCurrentInboxes(mapping)

      const excludeIds = Array.from(allUsedInboxIds).filter(
        (id) => !currentSelectedIds.includes(id)
      )
      mapping.sendFromMultiStore.setExcludeInboxIds(excludeIds)
    })
  }

  getMappings = async () => {
    try {
      const { data } = await IntegrationsApi.getMapInboxesToIntegrationChannels(
        this._integration.key
      )
      const mappings: IInboxSlackMapping[] = []

      Object.entries(data.mapping).forEach(([channelId, inboxIds]) => {
        const mappingId = nanoid()
        const sendFromMultiStore = this.createSendFromMultiStore(this.allTeamInboxes)

        mappings.push({
          id: mappingId,
          inboxes: inboxIds,
          slackChannelId: channelId,
          isSaved: true,
          sendFromMultiStore,
        })
      })

      if (mappings.length === 0) {
        const mappingId = nanoid()
        const sendFromMultiStore = this.createSendFromMultiStore(this.allTeamInboxes)

        mappings.push({
          id: mappingId,
          inboxes: [],
          slackChannelId: '',
          isSaved: false,
          sendFromMultiStore,
        })
      }

      this._mappings = mappings
      await Promise.all(mappings.map((mapping) => mapping.sendFromMultiStore.initPromise))
      for (const mapping of mappings) {
        if (mapping.inboxes.length > 0) {
          await mapping.sendFromMultiStore.setInboxIds(mapping.inboxes)
        }
      }

      this.updateExcludeInboxIds()
    } catch (e) {
      logger.error(e)
    }
  }

  addMapping = () => {
    if (!this.canAddMapping) return

    const mappingId = nanoid()
    const sendFromMultiStore = this.createSendFromMultiStore(this.allTeamInboxes)

    this._mappings.push({
      id: mappingId,
      inboxes: [],
      slackChannelId: '',
      isSaved: false,
      sendFromMultiStore,
    })

    sendFromMultiStore.initPromise.then(() => {
      this.updateExcludeInboxIds()
    })
  }

  updateLocalMapping = (mapping: IInboxSlackMapping, updates?: Partial<IInboxSlackMapping>) => {
    if (updates) {
      Object.assign(mapping, updates)
    }
    this.updateExcludeInboxIds()
  }

  updateMapping = (mapping: IInboxSlackMapping, updates: Partial<IInboxSlackMapping>) => {
    const oldInboxes = [...mapping.inboxes]
    const oldSlackChannelId = mapping.slackChannelId
    const originalInboxesForComparison = mapping.originalInboxes || oldInboxes

    this.updateLocalMapping(mapping, updates)

    if (mapping.isSaved && mapping.inboxes.length === 0) {
      mapping.error = 'At least one inbox needs to be selected to map to a Slack channel.'
    } else {
      mapping.error = ''
    }

    const isSameChannel = mapping.slackChannelId === oldSlackChannelId
    const isSameInboxes =
      JSON.stringify(mapping.inboxes) === JSON.stringify(originalInboxesForComparison)

    if (mapping.inboxes.length > 0 && mapping.slackChannelId) {
      if (mapping.isSaved) {
        if (!isSameChannel || !isSameInboxes) {
          this.createMappingOnServer(mapping, oldSlackChannelId)
        }
      } else {
        this.createMappingOnServer(mapping)
      }
      delete mapping.originalInboxes
    }
  }

  getSlackChannelSelectItems = (mapping: IInboxSlackMapping): IDropdownItem[] => {
    return this.slackChannels
      .filter((channel) => {
        const isUsedInOtherMappings = this.mappings.some(
          (value) => value.id !== mapping.id && value.slackChannelId === channel.id
        )
        return !isUsedInOtherMappings
      })
      .map((channel) => ({
        id: channel.id,
        label: channel.name,
        value: channel.id,
        iconL: 'hash2',
      }))
  }

  removeMapping = (mappingId: string) => {
    const mapping = this._mappings.find((value) => value.id === mappingId)
    if (!mapping) return

    if (mapping.isSaved) {
      this.showDeleteConfirmation(mapping)
    } else {
      this.removeUnsavedMapping(mappingId)
    }
  }

  removeUnsavedMapping = (mappingId: string) => {
    this._mappings = this._mappings.filter((value) => value.id !== mappingId)
    this.updateExcludeInboxIds()
  }

  private showDeleteConfirmation = (mapping: IInboxSlackMapping) => {
    const id = nanoid()
    modalStore.addModal({
      id,
      type: ModalTypeList.ALERT,
      title: 'Delete mapping?',
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: async () => {
          try {
            modalStore.closeModal(id)
            await this.removeSlackMapping(mapping)
          } catch (e) {
            logger.error(e)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(id)
        },
      },
    })
  }

  private removeSlackMapping = async (mapping: IInboxSlackMapping) => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await IntegrationsApi.removeSlackMapping(this._integration.key, mapping.slackChannelId)
      this.removeUnsavedMapping(mapping.id)

      toastStore.add({
        type: 'success',
        title: 'Your Salesmsg inbox to Slack mapping was updated.',
      })

      if (this.mappings.length === 0) {
        this.addMapping()
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Failed to delete mapping',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private createMappingOnServer = async (
    mapping: IInboxSlackMapping,
    oldSlackChannelId?: string
  ) => {
    try {
      runInAction(() => {
        this._loading = true
      })
      await IntegrationsApi.addMessengerMapping({
        integration: this._integration.key,
        inboxes: mapping.inboxes,
        channel_id: mapping.slackChannelId,
        channel_name: this.slackChannels.find((c) => c.id === mapping.slackChannelId)?.name || '',
        previous_channel_id: oldSlackChannelId || '',
      })

      runInAction(() => {
        mapping.isSaved = true
      })

      toastStore.add({
        type: 'success',
        title: oldSlackChannelId ? 'Mapping updated successfully' : 'Mapping created successfully',
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  refreshMappings = async () => {
    runInAction(() => {
      this._loading = true
    })
    this.getChannels()
    await this.getMappings()

    runInAction(() => {
      this._loading = false
    })
  }
}
